import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  CardActions,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  CardMedia,
  MobileStepper,
  useTheme,
  Tooltip
} from '@mui/material';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DevicesIcon from '@mui/icons-material/Devices';
import WebIcon from '@mui/icons-material/Web';
import BusinessIcon from '@mui/icons-material/Business';
import CloudIcon from '@mui/icons-material/Cloud';
import BuildIcon from '@mui/icons-material/Build';
import LaptopIcon from '@mui/icons-material/Laptop';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';
import CloseIcon from '@mui/icons-material/Close';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BarChartIcon from '@mui/icons-material/BarChart';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useLanguage } from '../context/LanguageContext';
import CountUp from 'react-countup';
import StatsIndicator from '../components/StatsIndicator';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SpeedIcon from '@mui/icons-material/Speed';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CodeIcon from '@mui/icons-material/Code';
import EmailIcon from '@mui/icons-material/Email';
import CategoryIcon from '@mui/icons-material/Category';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import CalculateIcon from '@mui/icons-material/Calculate';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';

const Home: React.FC = () => {
  const { t } = useLanguage();
  
  // Animation state
  const [animateStats, setAnimateStats] = useState(false);
  const [animateAbout, setAnimateAbout] = useState(false);
  const [animateHeroStats, setAnimateHeroStats] = useState(false);
  const [visibleServiceCards, setVisibleServiceCards] = useState<number[]>([]);
  const [servicesHeaderVisible, setServicesHeaderVisible] = useState(false);
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [isAnimating, setIsAnimating] = useState(false);
  const servicesSectionRef = useRef<HTMLDivElement>(null);
  const servicesHeaderRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  
  // Référence pour les vidéos
  const videoRef1 = useRef<HTMLVideoElement>(null);
  const videoRef2 = useRef<HTMLVideoElement>(null);
  const [activeVideo, setActiveVideo] = useState(1);
  
  // Services data
  const serviceItems = [
    {
      title: t('home.services.mobile.title'),
      description: t('home.services.mobile.description'),
      features: [
        t('home.services.mobile.features.0'),
        t('home.services.mobile.features.1'),
        t('home.services.mobile.features.2'),
        t('home.services.mobile.features.3'),
        t('home.services.mobile.features.4')
      ],
      icon: <DevicesIcon sx={{ fontSize: 48, color: '#015480' }} />,
      color: '#015480',
    },
    {
      title: t('home.services.web.title'),
      description: t('home.services.web.description'),
      features: [
        t('home.services.web.features.0'),
        t('home.services.web.features.1'),
        t('home.services.web.features.2'),
        t('home.services.web.features.3'),
        t('home.services.web.features.4')
      ],
      icon: <WebIcon sx={{ fontSize: 48, color: '#0288d1' }} />,
      color: '#0288d1',
    },
    {
      title: t('home.services.windows.title'),
      description: t('home.services.windows.description'),
      features: [
        t('home.services.windows.features.0'),
        t('home.services.windows.features.1'),
        t('home.services.windows.features.2'),
        t('home.services.windows.features.3'),
        t('home.services.windows.features.4')
      ],
      icon: <DesktopWindowsIcon sx={{ fontSize: 48, color: '#03a9f4' }} />,
      color: '#03a9f4',
    },
    {
      title: t('home.services.custom.title'),
      description: t('home.services.custom.description'),
      features: [
        t('home.services.custom.features.0'),
        t('home.services.custom.features.1'),
        t('home.services.custom.features.2'),
        t('home.services.custom.features.3'),
        t('home.services.custom.features.4')
      ],
      icon: <CodeIcon sx={{ fontSize: 48, color: '#4a148c' }} />,
      color: '#4a148c',
    },
    {
      title: t('home.services.maintenance.title'),
      description: t('home.services.maintenance.description'),
      features: [
        t('home.services.maintenance.features.0'),
        t('home.services.maintenance.features.1'),
        t('home.services.maintenance.features.2'),
        t('home.services.maintenance.features.3'),
        t('home.services.maintenance.features.4')
      ],
      icon: <BuildIcon sx={{ fontSize: 48, color: '#015480' }} />,
      color: '#015480',
    }
  ];
  
  // Gestionnaire pour la transition entre les vidéos
  const handleVideoTransition = () => {
    // Si la vidéo 1 est active et se termine
    if (activeVideo === 1) {
      // Démarre la vidéo 2 et la rend visible
      if (videoRef2.current) {
        // La vidéo 2 devrait déjà être prête à 0 grâce à l'effet timeupdate
        // S'assurer que la vitesse est toujours à 0.5x
        videoRef2.current.playbackRate = 0.5;
        videoRef2.current.play()
          .then(() => {
            // Une fois que la lecture a commencé, on change l'élément actif
            // Cela déclenche un fondu avec l'effet d'opacité
            setTimeout(() => {
              setActiveVideo(2);
            }, 100); // Un court délai pour s'assurer que la vidéo est en cours de lecture
          })
          .catch(err => {
            console.error('Erreur de lecture vidéo:', err);
            // Fallback en cas d'erreur
            setActiveVideo(2);
          });
      }
    } 
    // Si la vidéo 2 est active et se termine
    else {
      // Démarre la vidéo 1 et la rend visible
      if (videoRef1.current) {
        // La vidéo 1 devrait déjà être prête à 0 grâce à l'effet timeupdate
        // S'assurer que la vitesse est toujours à 0.5x
        videoRef1.current.playbackRate = 0.5;
        videoRef1.current.play()
          .then(() => {
            // Une fois que la lecture a commencé, on change l'élément actif
            // Cela déclenche un fondu avec l'effet d'opacité
            setTimeout(() => {
              setActiveVideo(1);
            }, 100); // Un court délai pour s'assurer que la vidéo est en cours de lecture
          })
          .catch(err => {
            console.error('Erreur de lecture vidéo:', err);
            // Fallback en cas d'erreur
            setActiveVideo(1);
          });
      }
    }
  };
  
  // Précharger les vidéos et les rendre prêtes pour une transition fluide
  useEffect(() => {
    // Préchargement des vidéos
    if (videoRef1.current && videoRef2.current) {
      // Charger les vidéos
      videoRef1.current.load();
      videoRef2.current.load();
      
      // Définir la vitesse de lecture à 0.5x
      videoRef1.current.playbackRate = 0.5;
      videoRef2.current.playbackRate = 0.5;
      
      // Gestionnaires pour maintenir la vitesse de lecture à 0.5x
      const handlePlay1 = () => {
        if (videoRef1.current) {
          videoRef1.current.playbackRate = 0.5;
        }
      };
      
      const handlePlay2 = () => {
        if (videoRef2.current) {
          videoRef2.current.playbackRate = 0.5;
        }
      };
      
      // Configurer les gestionnaires d'événements timeupdate
      const handleTimeUpdate1 = () => {
        // Si la vidéo 1 est proche de la fin (3 secondes avant la fin)
        if (videoRef1.current && 
            videoRef1.current.duration > 0 && 
            videoRef1.current.currentTime > videoRef1.current.duration - 3) {
          // Préparer la vidéo 2 pour la lecture
          if (videoRef2.current && activeVideo === 1) {
            videoRef2.current.currentTime = 0;
          }
        }
      };
      
      const handleTimeUpdate2 = () => {
        // Si la vidéo 2 est proche de la fin (3 secondes avant la fin)
        if (videoRef2.current && 
            videoRef2.current.duration > 0 && 
            videoRef2.current.currentTime > videoRef2.current.duration - 3) {
          // Préparer la vidéo 1 pour la lecture
          if (videoRef1.current && activeVideo === 2) {
            videoRef1.current.currentTime = 0;
          }
        }
      };
      
      // Ajouter les écouteurs d'événements
      videoRef1.current.addEventListener('timeupdate', handleTimeUpdate1);
      videoRef2.current.addEventListener('timeupdate', handleTimeUpdate2);
      videoRef1.current.addEventListener('play', handlePlay1);
      videoRef2.current.addEventListener('play', handlePlay2);
      
      // Nettoyer les écouteurs d'événements lors du démontage du composant
      return () => {
        if (videoRef1.current) {
          videoRef1.current.removeEventListener('timeupdate', handleTimeUpdate1);
          videoRef1.current.removeEventListener('play', handlePlay1);
        }
        if (videoRef2.current) {
          videoRef2.current.removeEventListener('timeupdate', handleTimeUpdate2);
          videoRef2.current.removeEventListener('play', handlePlay2);
        }
      };
    }
  }, [activeVideo]);
  
  // Trigger hero stats animation on component mount
  useEffect(() => {
    // Auto-trigger hero stats animation after a short delay
    const timer = setTimeout(() => {
      setAnimateHeroStats(true);
    }, 800);
    
    return () => {
      clearTimeout(timer);
    };
  }, []);
  
  // Intersection Observer for animations
  useEffect(() => {
    const statsObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setAnimateStats(true);
      }
    }, { threshold: 0.3 });
    
    const aboutObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setAnimateAbout(true);
      }
    }, { threshold: 0.3 });
    
    const heroStatsObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setAnimateHeroStats(true);
      }
    }, { threshold: 0.1 });
    
    const statsSection = document.getElementById('stats-section');
    const aboutSection = document.getElementById('about-section');
    const heroStatsSection = document.getElementById('hero-stats-section');
    
    if (statsSection) statsObserver.observe(statsSection);
    if (aboutSection) aboutObserver.observe(aboutSection);
    if (heroStatsSection) heroStatsObserver.observe(heroStatsSection);
    
    return () => {
      if (statsSection) statsObserver.unobserve(statsSection);
      if (aboutSection) aboutObserver.unobserve(aboutSection);
      if (heroStatsSection) heroStatsObserver.unobserve(heroStatsSection);
    };
  }, []);
  
  // Setup intersection observer for services section
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3
    };

    const handleServicesHeaderIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Header is visible, show animation
          setServicesHeaderVisible(true);
          entry.target.classList.add('animate-in');
        }
      });
    };
    
    const handleServiceCardIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.getAttribute('data-index') || '0');
          
          // Add a small delay before showing the card
          setTimeout(() => {
            setVisibleServiceCards(prev => {
              if (!prev.includes(index)) {
                return [...prev, index].sort((a, b) => a - b);
              }
              return prev;
            });
          }, 300); // Small delay for better user experience
          
          // Unobserve this card after it's been revealed
          cardObserver.unobserve(entry.target);
        }
      });
    };

    const headerObserver = new IntersectionObserver(handleServicesHeaderIntersect, observerOptions);
    const cardObserver = new IntersectionObserver(handleServiceCardIntersect, {
      ...observerOptions,
      threshold: 0.2
    });
    
    if (servicesHeaderRef.current) {
      headerObserver.observe(servicesHeaderRef.current);
    }
    
    // Need to wait for the DOM to be ready before querying elements
    setTimeout(() => {
      const serviceCards = document.querySelectorAll('.service-card-container');
      serviceCards.forEach(card => {
        cardObserver.observe(card);
      });
    }, 100);

    return () => {
      headerObserver.disconnect();
      cardObserver.disconnect();
    };
  }, []);
  

  
  // Contact form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [projectType, setProjectType] = useState('');
  const [message, setMessage] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  
  const handleProjectTypeChange = (event: SelectChangeEvent) => {
    setProjectType(event.target.value as string);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!name || !email || !message) {
      setFormError(true);
      return;
    }
    
    // Here you would typically send the form data to your backend
    console.log({ name, email, phone, company, projectType, message });
    
    // Show success message
    setFormSubmitted(true);
    
    // Reset form
    setName('');
    setEmail('');
    setPhone('');
    setCompany('');
    setProjectType('');
    setMessage('');
  };
  
  const handleCloseSnackbar = () => {
    setFormSubmitted(false);
    setFormError(false);
  };
  
  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };
  
  const features = [
    t('home.features.fastResponse'),
    t('home.features.experiencedAgents'),
    t('home.features.knowledgeBase'),
    t('home.features.availability')
  ];
  
  const services = [
    {
      title: getTranslationString('home.services.technicalSupport.title'),
      description: getTranslationString('home.services.technicalSupport.description'),
      icon: <SupportAgentIcon sx={{ fontSize: 40 }} />
    },
    {
      title: getTranslationString('home.services.accountManagement.title'),
      description: getTranslationString('home.services.accountManagement.description'),
      icon: <ManageAccountsIcon sx={{ fontSize: 40 }} />
    },
    {
      title: getTranslationString('home.services.cloudIntegration.title'),
      description: getTranslationString('home.services.cloudIntegration.description'),
      icon: <CloudIcon sx={{ fontSize: 40 }} />
    },
    {
      title: getTranslationString('home.services.applicationMaintenance.title'),
      description: getTranslationString('home.services.applicationMaintenance.description'),
      icon: <BuildIcon sx={{ fontSize: 40 }} />
    },
    {
      title: getTranslationString('home.services.dataAnalytics.title'),
      description: getTranslationString('home.services.dataAnalytics.description'),
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />
    },
    {
      title: getTranslationString('home.services.quickResponse.title'),
      description: getTranslationString('home.services.quickResponse.description'),
      icon: <SpeedIcon sx={{ fontSize: 40 }} />
    }
  ];

  const stats = [
    { value: 150, suffix: "+", label: "Projects Delivered" },
    { value: 98, suffix: "%", label: "Client Retention" },
    { value: 50, suffix: "+", label: "Enterprise Clients" },
    { value: 24, suffix: "/7", label: "Support Coverage" }
  ];
  
  // Testimonials section
  const testimonials = [
    {
      quote: getTranslationString('home.testimonials.quote1'),
      author: getTranslationString('home.testimonials.author1'),
      position: getTranslationString('home.testimonials.position1'),
      avatar: "https://randomuser.me/api/portraits/women/44.jpg"
    },
    {
      quote: getTranslationString('home.testimonials.quote2'),
      author: getTranslationString('home.testimonials.author2'),
      position: getTranslationString('home.testimonials.position2'),
      avatar: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      quote: getTranslationString('home.testimonials.quote3'),
      author: getTranslationString('home.testimonials.author3'),
      position: getTranslationString('home.testimonials.position3'),
      avatar: "https://randomuser.me/api/portraits/women/68.jpg"
    }
  ];
  
  const handleNextService = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection('right');
    setTimeout(() => {
      setActiveServiceIndex((prevIndex) => (prevIndex + 1) % serviceItems.length);
      setIsAnimating(false);
    }, 300);
  };

  const handlePrevService = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection('left');
    setTimeout(() => {
      setActiveServiceIndex((prevIndex) => (prevIndex - 1 + serviceItems.length) % serviceItems.length);
      setIsAnimating(false);
    }, 300);
  };

  const handleServiceClick = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection(index > activeServiceIndex ? 'right' : 'left');
    setTimeout(() => {
      setActiveServiceIndex(index);
      setIsAnimating(false);
    }, 300);
  };
  
  return (
    <Box>
      {/* Hero Section - Pleine hauteur ajustée pour le header */}
      <Paper 
        id="hero-section"
        sx={{
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          minHeight: 'calc(100vh - 64px)',
          paddingTop: 0,
          marginTop: 0,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          mb: 0,
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          color: 'white',
        }}
      >
        {/* Arrière-plan vidéo */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}>
          {/* Vidéo en arrière-plan */}
          <Box 
            component="div"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              zIndex: 0,
              '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(0, 0, 0, 0.65)',
                backdropFilter: 'blur(5px)',
                zIndex: 1
              }
            }}
          >
            {/* Première vidéo */}
            <Box 
              component="video"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'blur(8px)',
                transform: 'scale(1.05)',
                opacity: activeVideo === 1 ? 1 : 0,
                transition: 'opacity 0.8s ease-in-out',
                zIndex: 0,
              }}
              autoPlay
              muted
              loop
              ref={videoRef1}
              onEnded={handleVideoTransition}
              src="/resources/videospartiate.mp4"
              playsInline
            />
            
            {/* Deuxième vidéo (identique, utilisée pour une transition fluide) */}
            <Box 
              component="video"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'blur(8px)',
                transform: 'scale(1.05)',
                opacity: activeVideo === 2 ? 1 : 0,
                transition: 'opacity 0.8s ease-in-out',
                zIndex: 0,
              }}
              muted
              loop
              ref={videoRef2}
              onEnded={handleVideoTransition}
              src="/resources/videospartiate.mp4"
              playsInline
            />
          </Box>
        </Box>

                  <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 6, md: 0 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography 
                component="h1" 
                variant="h3" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2,
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  WebkitTextFillColor: 'white',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  letterSpacing: '0.5px',
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                  lineHeight: 1.2
                }}
              >
                {t('home.hero.title')}
              </Typography>
              <Typography 
                variant="h6" 
                paragraph
                sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  mb: 4,
                  lineHeight: 1.6,
                  maxWidth: { xs: '100%', md: '800px' },
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  fontWeight: 100
                }}
              >
                <Box component="span" sx={{ fontWeight: 100, color: 'white' }}>
                  {t('app.tagline')}
                </Box>
                <br />
                {t('app.subTagline')}
              </Typography>
              <Typography 
                variant="subtitle1"
                sx={{ 
                  color: 'rgba(255,255,255,0.85)',
                  mb: 2,
                  fontStyle: 'italic'
                }}
              >
                {t('home.features.availability')}
              </Typography>
              
              {/* Stats intégrés dans la section Hero - Améliorés avec animations */}
              <Box 
                id="hero-stats-section"
                sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 3, 
                mb: 4, 
                mt: 4,
                maxWidth: '100%',
                width: '100%',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -10,
                  left: 0,
                  width: '100%',
                  height: 'calc(100% + 20px)',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)',
                  borderRadius: 3,
                  zIndex: -1
                }
              }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(12px)',
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  flexGrow: 1,
                  flex: { xs: '1 1 45%', sm: '1 1 22%' },
                  minWidth: { xs: '140px', sm: '160px' },
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  position: 'relative',
                  opacity: 1,
                  height: { xs: '140px', sm: '160px' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 30px rgba(0,0,0,0.15)',
                    background: 'rgba(255,255,255,0.2)'
                  }
                }}>
                  {/* Graphique spécifique - Graphique en barres stylisé */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '20%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                    padding: '0 20px',
                    opacity: 0.15,
                    zIndex: 0
                  }}>
                    <Box sx={{
                      width: '15%',
                      height: '60%',
                      background: 'white',
                      animation: 'barGrow 1.5s ease-out forwards',
                      animationDelay: '0.5s',
                      transform: 'scaleY(0)',
                      transformOrigin: 'bottom',
                      '@keyframes barGrow': {
                        '0%': { transform: 'scaleY(0)' },
                        '100%': { transform: 'scaleY(1)' }
                      }
                    }} />
                    <Box sx={{
                      width: '15%',
                      height: '75%',
                      background: 'white',
                      mx: 0.5,
                      animation: 'barGrow 1.5s ease-out forwards',
                      animationDelay: '0.7s',
                      transform: 'scaleY(0)',
                      transformOrigin: 'bottom'
                    }} />
                    <Box sx={{
                      width: '15%',
                      height: '90%',
                      background: 'white',
                      animation: 'barGrow 1.5s ease-out forwards',
                      animationDelay: '0.9s',
                      transform: 'scaleY(0)',
                      transformOrigin: 'bottom'
                    }} />
                    <Box sx={{
                      width: '15%',
                      height: '100%',
                      background: 'white',
                      mx: 0.5,
                      animation: 'barGrow 1.5s ease-out forwards',
                      animationDelay: '1.1s',
                      transform: 'scaleY(0)',
                      transformOrigin: 'bottom'
                    }} />
                  </Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'white', 
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      fontSize: { xs: '2.2rem', md: '2.5rem' },
                      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                      background: 'linear-gradient(90deg, #ffffff 0%, #e0e0e0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    <CountUp
                      start={0}
                      end={150}
                      duration={2.5}
                      delay={0.3}
                      useEasing={true}
                      suffix="+"
                      preserveValue={true}
                    />
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)', 
                      fontWeight: 500,
                      position: 'relative',
                      zIndex: 1,
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      opacity: animateHeroStats ? 1 : 0,
                      height: animateHeroStats ? 'auto' : 0,
                      overflow: 'hidden',
                      transition: 'opacity 0.5s ease-in',
                      transitionDelay: '2.5s'
                    }}
                  >
                    {t('home.stats.projectsDelivered')}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(12px)',
                  p: 3, 
                  borderRadius: 3,
                  textAlign: 'center',
                  flexGrow: 1,
                  flex: { xs: '1 1 45%', sm: '1 1 22%' },
                  minWidth: { xs: '140px', sm: '160px' },
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  position: 'relative',
                  opacity: 1,
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 30px rgba(0,0,0,0.15)',
                    background: 'rgba(255,255,255,0.2)'
                  }
                }}>
                  {/* Graphique spécifique - Diagramme circulaire */}
                  <Box sx={{
                    position: 'absolute',
                    top: '10%',
                    right: '10%',
                    width: '35px',
                    height: '35px',
                    borderRadius: '50%',
                    background: 'conic-gradient(from 0deg, transparent 0%, transparent 2%, white 2%, white 98%, transparent 98%)',
                    opacity: 0.15,
                    animation: 'rotate 10s linear infinite, fillCircle 1.5s ease-out forwards',
                    animationDelay: '0s, 0.8s',
                    clipPath: 'circle(0% at 50% 50%)',
                    '@keyframes fillCircle': {
                      '0%': { clipPath: 'circle(0% at 50% 50%)' },
                      '100%': { clipPath: 'circle(50% at 50% 50%)' }
                    },
                    '@keyframes rotate': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }} />
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'white', 
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      fontSize: { xs: '2.2rem', md: '2.5rem' },
                      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                      background: 'linear-gradient(90deg, #ffffff 0%, #e0e0e0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    <CountUp
                      start={0}
                      end={98}
                      duration={2.5}
                      delay={0.5}
                      useEasing={true}
                      suffix="%"
                      preserveValue={true}
                    />
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)', 
                      fontWeight: 500,
                      position: 'relative',
                      zIndex: 1,
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      opacity: animateHeroStats ? 1 : 0,
                      height: animateHeroStats ? 'auto' : 0,
                      overflow: 'hidden',
                      transition: 'opacity 0.5s ease-in',
                      transitionDelay: '2.7s'
                    }}
                  >
                    {t('home.stats.clientRetention')}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(12px)',
                  p: 3, 
                  borderRadius: 3,
                  textAlign: 'center',
                  flexGrow: 1,
                  flex: { xs: '1 1 45%', sm: '1 1 22%' },
                  minWidth: { xs: '140px', sm: '160px' },
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  position: 'relative',
                  opacity: 1,
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 30px rgba(0,0,0,0.15)',
                    background: 'rgba(255,255,255,0.2)'
                  }
                }}>
                  {/* Graphique spécifique - Lignes de croissance */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: '15%',
                    left: 0,
                    width: '100%',
                    height: '30%',
                    opacity: 0.15,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: '0 10px',
                    zIndex: 0
                  }}>
                    <Box sx={{
                      position: 'relative',
                      width: '100%',
                      height: '1px',
                      background: 'rgba(255,255,255,0.5)',
                      mb: 1
                    }} />
                    <Box sx={{
                      width: '100%',
                      height: '30px',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent 0%, white 100%)',
                        clipPath: 'polygon(0 100%, 0 30%, 20% 40%, 40% 20%, 60% 50%, 80% 15%, 100% 30%, 100% 100%)',
                        animation: 'drawLine 2s forwards',
                        animationDelay: '1s',
                        transformOrigin: 'left',
                        transform: 'scaleX(0)',
                        '@keyframes drawLine': {
                          '0%': { transform: 'scaleX(0)' },
                          '100%': { transform: 'scaleX(1)' }
                        }
                      }
                    }} />
                  </Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'white', 
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      fontSize: { xs: '2.2rem', md: '2.5rem' },
                      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                      background: 'linear-gradient(90deg, #ffffff 0%, #e0e0e0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    <CountUp
                      start={0}
                      end={50}
                      duration={2.5}
                      delay={0.7}
                      useEasing={true}
                      suffix="+"
                      preserveValue={true}
                    />
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)', 
                      fontWeight: 500,
                      position: 'relative',
                      zIndex: 1,
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      opacity: animateHeroStats ? 1 : 0,
                      height: animateHeroStats ? 'auto' : 0,
                      overflow: 'hidden',
                      transition: 'opacity 0.5s ease-in',
                      transitionDelay: '2.9s'
                    }}
                  >
                    {t('home.stats.enterpriseClients')}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(12px)',
                  p: 3, 
                  borderRadius: 3,
                  textAlign: 'center',
                  flexGrow: 1,
                  flex: { xs: '1 1 45%', sm: '1 1 22%' },
                  minWidth: { xs: '140px', sm: '160px' },
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  position: 'relative',
                  opacity: 1,
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 30px rgba(0,0,0,0.15)',
                    background: 'rgba(255,255,255,0.2)'
                  }
                }}>
                  {/* Vraies icônes de message et téléphone qui se déplacent */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                    overflow: 'hidden'
                  }}>
                    {/* Icône de téléphone qui se déplace */}
                    <Box sx={{
                      position: 'absolute',
                      top: '30%',
                      left: '20%',
                      opacity: 0.3,
                      animation: 'movePhone 8s infinite linear',
                      '@keyframes movePhone': {
                        '0%': { transform: 'translate(0, 0) rotate(0deg)' },
                        '25%': { transform: 'translate(40px, 15px) rotate(20deg)' },
                        '50%': { transform: 'translate(60px, -10px) rotate(-10deg)' },
                        '75%': { transform: 'translate(20px, -25px) rotate(15deg)' },
                        '100%': { transform: 'translate(0, 0) rotate(0deg)' }
                      }
                    }}>
                      <PhoneIcon sx={{ 
                        fontSize: 24, 
                        color: 'white',
                        animation: 'pulse 2s infinite ease-in-out',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.2)' },
                          '100%': { transform: 'scale(1)' }
                      }
                    }} />
                    </Box>
                    
                    {/* Icône de message qui se déplace */}
                    <Box sx={{
                      position: 'absolute',
                      top: '60%',
                      right: '20%',
                      opacity: 0.3,
                      animation: 'moveMessage 7s infinite linear',
                      animationDelay: '1s',
                      '@keyframes moveMessage': {
                        '0%': { transform: 'translate(0, 0) rotate(0deg)' },
                        '25%': { transform: 'translate(-30px, -20px) rotate(-15deg)' },
                        '50%': { transform: 'translate(-50px, 5px) rotate(10deg)' },
                        '75%': { transform: 'translate(-20px, 25px) rotate(-5deg)' },
                        '100%': { transform: 'translate(0, 0) rotate(0deg)' }
                      }
                    }}>
                      <MessageIcon sx={{ 
                        fontSize: 22, 
                        color: 'white',
                        animation: 'glow 3s infinite ease-in-out',
                        '@keyframes glow': {
                          '0%': { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.6))' },
                          '50%': { filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.8))' },
                          '100%': { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.6))' }
                        }
                      }} />
                    </Box>
                    
                    {/* Deuxième icône de téléphone */}
                    <Box sx={{
                        position: 'absolute',
                      bottom: '20%',
                        left: '50%',
                      opacity: 0.25,
                      animation: 'movePhone2 9s infinite linear',
                      animationDelay: '0.5s',
                      '@keyframes movePhone2': {
                        '0%': { transform: 'translate(0, 0) rotate(10deg)' },
                        '33%': { transform: 'translate(-40px, -20px) rotate(-10deg)' },
                        '66%': { transform: 'translate(30px, -10px) rotate(20deg)' },
                        '100%': { transform: 'translate(0, 0) rotate(10deg)' }
                      }
                    }}>
                      <PhoneIcon sx={{ 
                        fontSize: 18, 
                        color: 'white',
                        animation: 'rotate 3s infinite ease-in-out',
                        '@keyframes rotate': {
                          '0%': { transform: 'rotate(0deg)' },
                          '25%': { transform: 'rotate(10deg)' },
                          '75%': { transform: 'rotate(-10deg)' },
                          '100%': { transform: 'rotate(0deg)' }
                        }
                      }} />
                    </Box>
                    
                    {/* Deuxième icône de message */}
                    <Box sx={{
                      position: 'absolute',
                      top: '15%',
                      right: '40%',
                      opacity: 0.25,
                      animation: 'moveMessage2 10s infinite linear',
                      animationDelay: '1.5s',
                      '@keyframes moveMessage2': {
                        '0%': { transform: 'translate(0, 0) rotate(-5deg)' },
                        '33%': { transform: 'translate(35px, 25px) rotate(5deg)' },
                        '66%': { transform: 'translate(-20px, 15px) rotate(-15deg)' },
                        '100%': { transform: 'translate(0, 0) rotate(-5deg)' }
                      }
                    }}>
                      <MessageIcon sx={{ 
                        fontSize: 18, 
                        color: 'white',
                        animation: 'fadeInOut 4s infinite ease-in-out',
                        '@keyframes fadeInOut': {
                          '0%': { opacity: 0.6 },
                          '50%': { opacity: 1 },
                          '100%': { opacity: 0.6 }
                        }
                      }} />
                    </Box>
                  </Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'white', 
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      fontSize: { xs: '2.2rem', md: '2.5rem' },
                      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                      background: 'linear-gradient(90deg, #ffffff 0%, #e0e0e0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    <CountUp
                      start={0}
                      end={24}
                      duration={2.5}
                      delay={0.9}
                      useEasing={true}
                      suffix="/7"
                      preserveValue={true}
                    />
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)', 
                      fontWeight: 500,
                      position: 'relative',
                      zIndex: 1,
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      opacity: animateHeroStats ? 1 : 0,
                      height: animateHeroStats ? 'auto' : 0,
                      overflow: 'hidden',
                      transition: 'opacity 0.5s ease-in',
                      transitionDelay: '3.1s'
                    }}
                  >
                    {t('home.stats.supportCoverage')}
                  </Typography>
                </Box>
              </Box>
              
              <Button 
                component={Link} 
                to="/myhub" 
                variant="contained" 
                color="secondary" 
                size="large"
                startIcon={<WebIcon />}
                sx={{ 
                  mt: 2,
                  fontWeight: '600',
                  px: 4, 
                  py: 1.5,
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  textTransform: 'none',
                  borderRadius: '8px',
                  letterSpacing: '0.3px',
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }
                }}
              >
                {t('home.hero.createTicket')}
              </Button>
              <Button 
                component={Link} 
                to="#services" 
                variant="outlined" 
                color="inherit" 
                size="large"
                startIcon={<BusinessIcon />}
                sx={{ 
                  mt: 2,
                  ml: { xs: 0, sm: 2 },
                  display: { xs: 'block', sm: 'inline-flex' },
                  width: { xs: '100%', sm: 'auto' },
                  fontWeight: '600',
                  px: 4, 
                  py: 1.5,
                  borderColor: 'rgba(255,255,255,0.5)',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  textTransform: 'none',
                  borderRadius: '8px',
                  letterSpacing: '0.3px',
                  fontSize: '1rem',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                {t('home.hero.viewWork')}
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ mt: { xs: 6, md: 0 } }}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  p: 4,
                  borderRadius: 4,
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                  },

                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    mb: 3, 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    letterSpacing: '0.5px',
                      fontSize: '1.5rem',
                      fontWeight: 500,
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      position: 'relative',
                      paddingBottom: '12px',
                    '&:after': {
                      content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '60px',
                        height: '2px',
                        background: 'linear-gradient(90deg, #ffffff, rgba(255,255,255,0.2))'
                      }
                    }}
                >
                    {t('home.platform.title')}
                </Typography>
                                  <List sx={{ mt: 2 }}>
                  {[
                    {
                      text: t('home.platform.deployment'),
                      icon: <DevicesIcon sx={{ color: '#64b5f6' }} />
                    },
                    {
                      text: t('home.platform.compatibility'),
                      icon: <WebIcon sx={{ color: '#81c784' }} />
                    },
                    {
                      text: t('home.platform.documentation'),
                      icon: <AssessmentIcon sx={{ color: '#ba68c8' }} />
                    },
                    {
                      text: t('home.platform.support'),
                      icon: <SupportAgentIcon sx={{ color: '#ffb74d' }} />
                    }
                  ].map((item, index) => (
                    <ListItem 
                      key={index} 
                      disableGutters 
                      sx={{ 
                        mb: 2,
                        position: 'relative',
                        pl: 1,
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          transform: 'translateX(5px)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 42,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 42,
                        width: 42,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(5px)',
                        mr: 2
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        primaryTypographyProps={{ 
                          sx: { 
                            color: 'white',
                            fontWeight: 100,
                            fontSize: '1.1rem',
                            letterSpacing: '0.3px',
                            fontFamily: '"Plus Jakarta Sans", sans-serif'
                          } 
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  sx={{
                    mt: 2,
                    py: 1.5,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: '#015480',
                    fontWeight: 300,
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    borderRadius: '8px',
                    letterSpacing: '0.3px',
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'white',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {t('home.platform.learnMore')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Paper>

      {/* Services Section */}
      <Box 
        component="section" 
        id="services" 
        sx={{ 
          py: { xs: 8, md: 0 },
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e8f4ff 100%)',
          position: 'relative',
          overflow: 'hidden',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/resources/scene1.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.05,
            zIndex: 0
          }
        }}
        ref={servicesSectionRef}
      >
        {/* Logos défilant horizontalement en arrière-plan */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: '5%',
            left: 0,
            width: '200%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            opacity: 0.08,
            zIndex: 1,
            animation: 'scrollLogosRight 120s linear infinite',
            '@keyframes scrollLogosRight': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-50%)' }
            }
          }}
        >
          {/* Premier groupe de logos */}
          <Box sx={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Box component="img" src="/resources/logo/windows.png" alt="Logo Windows" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/linux.png" alt="Logo Linux" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/applestore.png" alt="Logo Apple Store" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/playstore.png" alt="Logo Play Store" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/web.png" alt="Logo Web" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/firebase.png" alt="Logo Firebase" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/chatgpt.png" alt="Logo ChatGPT" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/supabase.jpg" alt="Logo Supabase" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/node.jpg" alt="Logo Node.js" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/mongodb.jpg" alt="Logo MongoDB" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/react.png" alt="Logo React" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/angular.svg" alt="Logo Angular" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/js.jpg" alt="Logo JavaScript" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Python-Logo.jpg" alt="Logo Python" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c++.png" alt="Logo C++" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/swift.png" alt="Logo Swift" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Dart_(programming_language)-Logo.wine.png" alt="Logo Dart" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/claude.jpg" alt="Logo Claude" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/cursor logo-words.jpg" alt="Logo Cursor" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c823e53b3a1a7b0d36a9.png" alt="Logo C#" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/java.png" alt="Logo Java" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/php.png" alt="Logo PHP" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/sql.png" alt="Logo SQL" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/maria.png" alt="Logo MariaDB" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Apache-Logo.png" alt="Logo Apache" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/wamp.jpg" alt="Logo WAMP" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
          </Box>
          {/* Duplication des logos pour un défilement continu */}
          <Box sx={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Box component="img" src="/resources/logo/windows.png" alt="Logo Windows" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/linux.png" alt="Logo Linux" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/applestore.png" alt="Logo Apple Store" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/playstore.png" alt="Logo Play Store" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/web.png" alt="Logo Web" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/firebase.png" alt="Logo Firebase" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/chatgpt.png" alt="Logo ChatGPT" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/supabase.jpg" alt="Logo Supabase" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/node.jpg" alt="Logo Node.js" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/mongodb.jpg" alt="Logo MongoDB" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/react.png" alt="Logo React" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/angular.svg" alt="Logo Angular" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/js.jpg" alt="Logo JavaScript" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Python-Logo.jpg" alt="Logo Python" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c++.png" alt="Logo C++" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/swift.png" alt="Logo Swift" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Dart_(programming_language)-Logo.wine.png" alt="Logo Dart" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/claude.jpg" alt="Logo Claude" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/cursor logo-words.jpg" alt="Logo Cursor" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c823e53b3a1a7b0d36a9.png" alt="Logo C#" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/java.png" alt="Logo Java" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/php.png" alt="Logo PHP" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/sql.png" alt="Logo SQL" sx={{ height: '65px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/maria.png" alt="Logo MariaDB" sx={{ height: '60px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Apache-Logo.png" alt="Logo Apache" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/wamp.jpg" alt="Logo WAMP" sx={{ height: '60px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
          </Box>
        </Box>

        {/* Troisième ligne d'animation - Nouveaux logos au milieu */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: '25%',
            left: 0,
            width: '200%',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            opacity: 0.06,
            zIndex: 1,
            animation: 'scrollLogosCenter 150s linear infinite',
            '@keyframes scrollLogosCenter': {
              '0%': { transform: 'translateX(-50%)' },
              '100%': { transform: 'translateX(0)' }
            }
          }}
        >
          {/* Premier groupe de nouveaux logos */}
          <Box sx={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Box component="img" src="/resources/logo/java.png" alt="Logo Java" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/php.png" alt="Logo PHP" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/sql.png" alt="Logo SQL" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/maria.png" alt="Logo MariaDB" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Apache-Logo.png" alt="Logo Apache" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/wamp.jpg" alt="Logo WAMP" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c823e53b3a1a7b0d36a9.png" alt="Logo C#" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/cursor logo-words.jpg" alt="Logo Cursor" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/claude.jpg" alt="Logo Claude" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Dart_(programming_language)-Logo.wine.png" alt="Logo Dart" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/swift.png" alt="Logo Swift" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c++.png" alt="Logo C++" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Python-Logo.jpg" alt="Logo Python" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/js.jpg" alt="Logo JavaScript" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/angular.svg" alt="Logo Angular" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/react.png" alt="Logo React" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/mongodb.jpg" alt="Logo MongoDB" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/node.jpg" alt="Logo Node.js" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/supabase.jpg" alt="Logo Supabase" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/chatgpt.png" alt="Logo ChatGPT" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/firebase.png" alt="Logo Firebase" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/web.png" alt="Logo Web" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/playstore.png" alt="Logo Play Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/applestore.png" alt="Logo Apple Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/linux.png" alt="Logo Linux" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/windows.png" alt="Logo Windows" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
          </Box>
          {/* Duplication des logos pour un défilement continu */}
          <Box sx={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Box component="img" src="/resources/logo/java.png" alt="Logo Java" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/php.png" alt="Logo PHP" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/sql.png" alt="Logo SQL" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/maria.png" alt="Logo MariaDB" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Apache-Logo.png" alt="Logo Apache" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/wamp.jpg" alt="Logo WAMP" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c823e53b3a1a7b0d36a9.png" alt="Logo C#" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/cursor logo-words.jpg" alt="Logo Cursor" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/claude.jpg" alt="Logo Claude" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Dart_(programming_language)-Logo.wine.png" alt="Logo Dart" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/swift.png" alt="Logo Swift" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c++.png" alt="Logo C++" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Python-Logo.jpg" alt="Logo Python" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/js.jpg" alt="Logo JavaScript" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/angular.svg" alt="Logo Angular" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/react.png" alt="Logo React" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/mongodb.jpg" alt="Logo MongoDB" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/node.jpg" alt="Logo Node.js" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/supabase.jpg" alt="Logo Supabase" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/chatgpt.png" alt="Logo ChatGPT" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/firebase.png" alt="Logo Firebase" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/web.png" alt="Logo Web" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/playstore.png" alt="Logo Play Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/applestore.png" alt="Logo Apple Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/linux.png" alt="Logo Linux" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/windows.png" alt="Logo Windows" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
          </Box>
        </Box>

        {/* Second défilement de logos en sens inverse avec différents logos et tailles */}
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: '10%',
            left: 0,
            width: '200%',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            opacity: 0.08,
            zIndex: 1,
            animation: 'scrollLogosLeft 90s linear infinite',
            '@keyframes scrollLogosLeft': {
              '0%': { transform: 'translateX(-50%)' },
              '100%': { transform: 'translateX(0)' }
            }
          }}
        >
          {/* Premier groupe de logos (sens inverse) */}
          <Box sx={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Box component="img" src="/resources/logo/wamp.jpg" alt="Logo WAMP" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Apache-Logo.png" alt="Logo Apache" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/maria.png" alt="Logo MariaDB" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/sql.png" alt="Logo SQL" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/php.png" alt="Logo PHP" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/java.png" alt="Logo Java" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c823e53b3a1a7b0d36a9.png" alt="Logo C#" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/cursor logo-words.jpg" alt="Logo Cursor" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/claude.jpg" alt="Logo Claude" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Dart_(programming_language)-Logo.wine.png" alt="Logo Dart" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/swift.png" alt="Logo Swift" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c++.png" alt="Logo C++" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Python-Logo.jpg" alt="Logo Python" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/js.jpg" alt="Logo JavaScript" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/angular.svg" alt="Logo Angular" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/react.png" alt="Logo React" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/mongodb.jpg" alt="Logo MongoDB" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/node.jpg" alt="Logo Node.js" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/supabase.jpg" alt="Logo Supabase" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/chatgpt.png" alt="Logo ChatGPT" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/firebase.png" alt="Logo Firebase" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/web.png" alt="Logo Web" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/playstore.png" alt="Logo Play Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/applestore.png" alt="Logo Apple Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/linux.png" alt="Logo Linux" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/windows.png" alt="Logo Windows" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
          </Box>
          {/* Duplication des logos pour un défilement continu (sens inverse) */}
          <Box sx={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Box component="img" src="/resources/logo/wamp.jpg" alt="Logo WAMP" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Apache-Logo.png" alt="Logo Apache" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/maria.png" alt="Logo MariaDB" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/sql.png" alt="Logo SQL" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/php.png" alt="Logo PHP" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/java.png" alt="Logo Java" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c823e53b3a1a7b0d36a9.png" alt="Logo C#" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/cursor logo-words.jpg" alt="Logo Cursor" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/claude.jpg" alt="Logo Claude" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Dart_(programming_language)-Logo.wine.png" alt="Logo Dart" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/swift.png" alt="Logo Swift" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/c++.png" alt="Logo C++" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/Python-Logo.jpg" alt="Logo Python" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/js.jpg" alt="Logo JavaScript" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/angular.svg" alt="Logo Angular" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/react.png" alt="Logo React" sx={{ height: '75px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/mongodb.jpg" alt="Logo MongoDB" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/node.jpg" alt="Logo Node.js" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/supabase.jpg" alt="Logo Supabase" sx={{ height: '70px', objectFit: 'contain', borderRadius: '8px', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/chatgpt.png" alt="Logo ChatGPT" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/firebase.png" alt="Logo Firebase" sx={{ height: '80px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/web.png" alt="Logo Web" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/playstore.png" alt="Logo Play Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/applestore.png" alt="Logo Apple Store" sx={{ height: '70px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/linux.png" alt="Logo Linux" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
            <Box component="img" src="/resources/logo/windows.png" alt="Logo Windows" sx={{ height: '90px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
          </Box>
        </Box>
        
        {/* Decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: '10%', 
            left: '5%', 
            width: '200px', 
            height: '200px', 
            borderRadius: '50%', 
            background: 'radial-gradient(circle, rgba(2, 136, 209, 0.1) 0%, rgba(2, 136, 209, 0) 70%)',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-20px)' }
            }
          }} 
        />
        <Box 
          sx={{ 
            position: 'absolute', 
            bottom: '15%', 
            right: '10%', 
            width: '150px', 
            height: '150px', 
            borderRadius: '50%', 
            background: 'radial-gradient(circle, rgba(1, 84, 128, 0.08) 0%, rgba(1, 84, 128, 0) 70%)',
            animation: 'float 6s ease-in-out infinite 1s',
          }} 
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Services Header */}
          <Box
            ref={servicesHeaderRef}
            sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: { xs: 4, md: 6 },
              opacity: servicesHeaderVisible ? 1 : 0,
              transform: servicesHeaderVisible ? 'translateY(0)' : 'translateY(30px)',
              transitionProperty: 'opacity, transform',
              transitionDuration: '0.8s',
              transitionTimingFunction: 'ease',
              '&.animate-in': {
                opacity: 1,
                transform: 'translateY(0)'
              }
            }}
          >
            <Box sx={{ 
              textAlign: { xs: 'center', md: 'left' }, 
              mb: { xs: 3, md: 0 }, 
              maxWidth: '600px',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: { xs: '50%', md: 0 },
                transform: { xs: 'translateX(-50%)', md: 'translateX(0)' },
                width: { xs: '80px', md: '120px' },
                height: '4px',
                background: 'linear-gradient(90deg, #015480, #0288d1)',
                borderRadius: '2px'
              }
            }}>
            <Typography 
                variant="h2" 
              component="h2" 
              sx={{ 
                  fontWeight: 700,
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                letterSpacing: '1px',
                  mb: 1,
                  background: 'linear-gradient(90deg, #015480, #0288d1)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                }}
              >
                {t('home.services.title')}
            </Typography>
              
            <Typography 
                variant="h4"
                color="text.secondary"
              sx={{ 
                  mb: 4,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2.2rem' },
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  whiteSpace: 'nowrap',
                  textAlign: 'center'
                }}
              >
                {t('home.services.subtitle')}
            </Typography>
          </Box>
          
            <Button
              component={Link}
              to="#contact"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '30px',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 14px rgba(2, 136, 209, 0.4)',
                background: 'linear-gradient(90deg, #015480, #0288d1)',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(2, 136, 209, 0.6)',
                  background: 'linear-gradient(90deg, #0288d1, #015480)'
                }
              }}
            >
              {t('home.services.discoverButton')}
            </Button>
          </Box>
          
          {/* Services Carousel */}
          <Box 
            sx={{ 
              mt: 2,
              maxWidth: '100%',
              position: 'relative',
              overflow: 'visible'
            }}
          >
            {/* Service Detail Card */}
            <Card 
              className="service-card-container"
              elevation={4}
                sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                  overflow: 'hidden',
                borderRadius: '16px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.7)',
                transition: 'all 0.3s ease',
                minHeight: { xs: '400px', md: '450px' },
                boxShadow: `0 15px 30px ${serviceItems[activeServiceIndex].color}25`,
                mb: 6,
                transform: isAnimating 
                  ? slideDirection === 'right' 
                    ? 'translateX(100%)' 
                    : 'translateX(-100%)'
                  : 'translateX(0)',
                opacity: isAnimating ? 0 : 1,
              }}
            >
              {/* Icon Column - Visible on md and up */}
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                p: 3, 
                bgcolor: serviceItems[activeServiceIndex].color,
                color: 'white',
                width: { md: '220px', lg: '280px' },
                minWidth: '220px',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                    position: 'absolute',
                  width: '200%',
                  height: '200%',
                  top: '-50%',
                  left: '-50%',
                  background: `radial-gradient(circle, ${serviceItems[activeServiceIndex].color}80 0%, ${serviceItems[activeServiceIndex].color} 70%)`,
                  opacity: 0.6,
                  zIndex: 0
                }
              }}>
                <Box sx={{ position: 'relative', zIndex: 2, width: '100%' }}>
                  <Box 
                    className="service-icon"
                  sx={{
                      width: { md: 100, lg: 120 },
                      height: { md: 100, lg: 120 },
                      mx: 'auto',
                      borderRadius: '50%',
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                    display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
                      border: '4px solid rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                  >
                    {React.cloneElement(serviceItems[activeServiceIndex].icon, { 
                      sx: { fontSize: { md: 50, lg: 64 }, color: 'white' } 
                    })}
                  </Box>
                  
                  {/* Service Name */}
                  <Typography 
                    variant="h6" 
                    component="h3"
                    sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      mb: 1,
                      fontSize: { md: '1rem', lg: '1.1rem' },
                      px: 1,
                      pr: 2,
                      wordBreak: 'break-word',
                      width: '100%',
                      maxWidth: '95%',
                      whiteSpace: 'normal',
                      lineHeight: 1.15
                    }}
                  >
                    {serviceItems[activeServiceIndex].title}
                  </Typography>
              </Box>
                  </Box>
                  
              <Box sx={{ 
                flexGrow: 1,
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: { xs: 3, md: 4 }
              }}>
                {/* Service Title - Only on mobile */}
                <Box sx={{ 
                  mb: 2, 
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center'
                }}>
                  <Box sx={{
                    mr: 2,
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: serviceItems[activeServiceIndex].color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(serviceItems[activeServiceIndex].icon, { sx: { fontSize: 30, color: 'white' } })}
                  </Box>
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      color: serviceItems[activeServiceIndex].color
                    }}
                  >
                    {serviceItems[activeServiceIndex].title}
                  </Typography>
                </Box>
                  
                {/* Service Description */}
                  <Typography
                  variant="subtitle1" 
                  paragraph 
                    sx={{ 
                    mb: 3,
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.7,
                      color: 'text.secondary',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    maxHeight: { xs: 'auto', md: '220px' },
                    overflow: { xs: 'visible', md: 'auto' },
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: serviceItems[activeServiceIndex].color + '80',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: serviceItems[activeServiceIndex].color,
                    }
                  }}
                >
                  {serviceItems[activeServiceIndex].description}
                  </Typography>
                  
                {/* Service Features */}
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 2,
                      fontWeight: 600,
                      color: serviceItems[activeServiceIndex].color
                    }}
                  >
                    {t('home.services.whatWeOffer')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {serviceItems[activeServiceIndex].features.map((feature, featureIndex) => (
                      <Box 
                        key={featureIndex} 
                      sx={{ 
                          flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)' },
                        display: 'flex', 
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: `${serviceItems[activeServiceIndex].color}10`,
                            transform: 'translateX(5px)'
                          }
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ 
                          mr: 1.5, 
                          color: serviceItems[activeServiceIndex].color,
                          fontSize: 20
                        }} />
                        <Typography variant="body2" sx={{ fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif' }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                
                {/* CTA Button */}
                      <Button 
                  component={Link}
                  to="#contact"
                  variant="outlined"
                  color="inherit"
                        sx={{ 
                    alignSelf: 'flex-start',
                    mt: 2,
                    borderColor: serviceItems[activeServiceIndex].color,
                    color: serviceItems[activeServiceIndex].color,
                          '&:hover': {
                      borderColor: serviceItems[activeServiceIndex].color,
                      bgcolor: `${serviceItems[activeServiceIndex].color}10`
                          }
                        }}
                      >
                  {t('home.services.learnMoreButton')}
                      </Button>
                    </Box>
            </Card>
            
            {/* Carousel Controls */}
            <MobileStepper
              steps={serviceItems.length}
              position="static"
              activeStep={activeServiceIndex}
              sx={{
                width: '100%',
                justifyContent: 'center',
                bgcolor: 'transparent',
                '& .MuiMobileStepper-dot': {
                  width: 12,
                  height: 12,
                  mx: 0.5
                },
                '& .MuiMobileStepper-dotActive': {
                  bgcolor: serviceItems[activeServiceIndex].color
                }
              }}
              nextButton={
                <IconButton
                  size="large"
                  onClick={handleNextService}
                  disabled={activeServiceIndex === serviceItems.length - 1}
                  sx={{
                    bgcolor: 'white',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)'
                    }
                  }}
                >
                  <KeyboardArrowRight />
                </IconButton>
              }
              backButton={
                <IconButton
                  size="large"
                  onClick={handlePrevService}
                  disabled={activeServiceIndex === 0}
                  sx={{
                    bgcolor: 'white',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)'
                    }
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>
              }
            />
            
            {/* Service Thumbnails for Quick Navigation */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mt: 2
            }}>
              {serviceItems.map((service, index) => (
                <Chip
                  key={index}
                  icon={React.cloneElement(service.icon, { sx: { fontSize: 16 } })}
                  label={service.title}
                  onClick={() => handleServiceClick(index)}
                  variant={index === activeServiceIndex ? "filled" : "outlined"}
                  color={index === activeServiceIndex ? "primary" : "default"}
                  sx={{
                    borderColor: index === activeServiceIndex ? service.color : 'rgba(0,0,0,0.23)',
                    bgcolor: index === activeServiceIndex ? service.color : 'transparent',
                    color: index === activeServiceIndex ? 'white' : 'text.primary',
                    '&:hover': {
                      bgcolor: index === activeServiceIndex ? service.color : `${service.color}20`
                    },
                    py: 1,
                    px: 0.5
                  }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Simulator Section - Strategically placed between Services and About */}
      <Box 
        id="simulator"
        sx={{ 
        position: 'relative',
        py: { xs: 6, md: 0 },
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #002f49 0%, #01579b 100%)',
      }}>
        {/* Video background with overlay */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(0,47,73,0.9) 0%, rgba(1,87,155,0.85) 100%)',
          }
        }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.4
            }}
          >
            <source src="/resources/simulator.mp4" type="video/mp4" />
            <source src="/resources/contact.mp4" type="video/mp4" />
          </video>
        </Box>
        
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          animation: 'float 8s ease-in-out infinite',
        }} />
        
        <Box sx={{
          position: 'absolute',
          bottom: '15%',
          right: '8%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 70%)',
          animation: 'float 10s ease-in-out infinite 1s',
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 700,
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                {t('home.simulator.title')}
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 2,
                  fontWeight: 500,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}
              >
                {t('home.simulator.subtitle')}
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  mb: 4,
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  maxWidth: '600px'
                }}
              >
                {t('home.simulator.description')}
              </Typography>
              
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 4 }}>
                  <Button
                  component={Link}
                  to="/cost-simulator"
                  variant="outlined"
                  color="inherit"
                  size="large"
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontWeight: 600,
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  {t('home.simulator.fullVersion')}
                </Button>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 7 }}>
              {/* Browser-like UI for the simulator */}
              <Box
                sx={{
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                  background: '#fff',
                  maxWidth: '100%',
                  mx: 'auto',
                }}
              >
                {/* Browser chrome/header */}
                <Box
                  sx={{
                    bgcolor: '#f0f4f8',
                    borderBottom: '1px solid #ddd',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {/* Browser controls */}
                  <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#febc2e' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28c840' }} />
                  </Box>
                  
                  {/* URL bar */}
                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: '#e4e9f0',
                      borderRadius: '4px',
                      py: 0.5,
                      px: 2,
                      fontSize: '0.8rem',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ color: '#0288d1', mr: 1 }}>
                      <LockIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      https://</Box>
                    <Box component="span">arescodeworks.com/cost-simulator</Box>
                  </Box>
                </Box>
                
                {/* Browser content */}
                <Box sx={{ p: 0 }}>
                  <MiniSimulator />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about-section" sx={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #edf7ff 100%)',
        position: 'relative',
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        borderTop: 1, 
        borderBottom: 1, 
        borderColor: 'divider',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/resources/scene1.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.03,
          zIndex: 0
        }
      }}>
        {/* Decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: '15%', 
            right: '8%', 
            width: '180px', 
            height: '180px', 
            borderRadius: '50%', 
            background: 'radial-gradient(circle, rgba(1, 84, 128, 0.08) 0%, rgba(1, 84, 128, 0) 70%)',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-20px)' }
            }
          }} 
        />
        <Box 
          sx={{ 
            position: 'absolute', 
            bottom: '10%', 
            left: '7%', 
            width: '140px', 
            height: '140px', 
            borderRadius: '50%', 
            background: 'radial-gradient(circle, rgba(2, 136, 209, 0.1) 0%, rgba(2, 136, 209, 0) 70%)',
            animation: 'float 6s ease-in-out infinite 1s',
          }} 
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 700,
                  mb: 3,
                  background: 'linear-gradient(90deg, #015480, #0288d1)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  pb: 1,
                  borderBottom: '2px solid #0288d1',
                  display: 'inline-block',
                  fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2.2rem' }
                }}
              >
                {t('home.about.title')}
              </Typography>
              
              <Typography 
                variant="body1" 
                paragraph 
                color="text.secondary" 
                sx={{ 
                  mb: 4,
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  maxWidth: '90%'
                }}
              >
                {t('home.about.description')}
              </Typography>
              
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 3,
                mt: 4
              }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderLeft: '4px solid #015480',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 28px rgba(1, 84, 128, 0.12)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ color: '#015480', mr: 2, fontSize: 28 }} />
                    <Typography 
                      variant="body1" 
                      fontWeight={600} 
                      sx={{ 
                        color: '#015480',
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      }}
                    >
                      {t('home.about.agileDevelopment')}
                    </Typography>
                  </Box>
                </Paper>
                
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderLeft: '4px solid #4a148c',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 28px rgba(74, 20, 140, 0.12)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WebIcon sx={{ color: '#4a148c', mr: 2, fontSize: 28 }} />
                    <Typography 
                      variant="body1" 
                      fontWeight={600} 
                      sx={{ 
                        color: '#4a148c',
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      }}
                    >
                      {t('home.about.securityFirst')}
                    </Typography>
                  </Box>
                </Paper>
                
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderLeft: '4px solid #004d40',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 28px rgba(0, 77, 64, 0.12)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CloudIcon sx={{ color: '#004d40', mr: 2, fontSize: 28 }} />
                    <Typography 
                      variant="body1" 
                      fontWeight={600} 
                      sx={{ 
                        color: '#004d40',
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      }}
                    >
                      {t('home.about.scalableArchitecture')}
                    </Typography>
                  </Box>
                </Paper>
                
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderLeft: '4px solid #bf360c',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 28px rgba(191, 54, 12, 0.12)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BuildIcon sx={{ color: '#bf360c', mr: 2, fontSize: 28 }} />
                    <Typography 
                      variant="body1" 
                      fontWeight={600} 
                      sx={{ 
                        color: '#bf360c',
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      }}
                    >
                      {t('home.about.support247')}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 3, md: 4 }, 
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(15px)',
                  borderTop: '4px solid #015480',
                  boxShadow: '0 15px 35px rgba(1, 84, 128, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 3, 
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    fontWeight: 600,
                    color: '#015480',
                    textAlign: 'center',
                    fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60px',
                      height: '3px',
                      background: 'linear-gradient(90deg, #015480, #0288d1)',
                      borderRadius: '3px'
                    }
                  }}
                >
                  {t('home.about.byTheNumbers')}
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6 }}>
                    <Box 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center', 
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(1, 84, 128, 0.05)',
                        mb: 3,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 700, 
                          color: 'primary.main',
                          mb: 1,
                          fontFamily: '"Orbitron", "Rajdhani", sans-serif'
                        }}
                      >
                        {animateAbout ? (
                          <CountUp 
                            start={0} 
                            end={8} 
                            duration={2} 
                            delay={0.2}
                            suffix="+"
                          />
                        ) : "8+"}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          fontFamily: '"Inter", "Roboto", sans-serif',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500
                        }}
                      >
                        {t('home.about.yearsExperience')}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center', 
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(1, 84, 128, 0.05)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#4a148c',
                          mb: 1,
                          fontFamily: '"Orbitron", "Rajdhani", sans-serif'
                        }}
                      >
                        {animateAbout ? (
                          <CountUp 
                            start={0} 
                            end={25} 
                            duration={2} 
                            delay={0.4}
                            suffix="+"
                          />
                        ) : "25+"}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          fontFamily: '"Inter", "Roboto", sans-serif',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500
                        }}
                      >
                        {t('home.about.teamMembers')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center', 
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(1, 84, 128, 0.05)',
                        mb: 3,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#004d40',
                          mb: 1,
                          fontFamily: '"Orbitron", "Rajdhani", sans-serif'
                        }}
                      >
                        {animateAbout ? (
                          <CountUp 
                            start={0} 
                            end={15} 
                            duration={2} 
                            delay={0.6}
                            suffix="+"
                          />
                        ) : "15+"}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          fontFamily: '"Inter", "Roboto", sans-serif',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500
                        }}
                      >
                        {t('home.about.industriesServed')}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        p: 3, 
                        textAlign: 'center', 
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(1, 84, 128, 0.05)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#bf360c',
                          mb: 1,
                          fontFamily: '"Orbitron", "Rajdhani", sans-serif'
                        }}
                      >
                        {animateAbout ? (
                          <CountUp 
                            start={99} 
                            end={99.9} 
                            duration={2} 
                            delay={0.8}
                            decimals={1}
                            suffix="%"
                          />
                        ) : "99.9%"}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          fontFamily: '"Inter", "Roboto", sans-serif',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500
                        }}
                      >
                        {t('home.about.uptimeSLA')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action with Contact Form */}
      <Box 
        id="contact"
        sx={{ 
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          minHeight: 'calc(100vh - 64px)',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        {/* Arrière-plan vidéo */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}>
          {/* Vidéo en arrière-plan */}
          <Box 
            component="div"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              zIndex: 0,
              '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(0, 0, 0, 0.65)',
                backdropFilter: 'blur(5px)',
                zIndex: 1
              }
            }}
          >
            <Box 
              component="video"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'blur(8px)',
                transform: 'scale(1.05)',
                zIndex: 0,
              }}
              autoPlay
              muted
              loop
              src="/resources/contact.mp4"
              playsInline
            />
          </Box>
        </Box>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 6, md: 0 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <HeadsetMicIcon sx={{ 
                  fontSize: 80, 
                  color: 'white',
                  mb: 2,
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                }} />
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                    color: 'white',
                    mb: 0,
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    letterSpacing: '0.5px',
                    fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2.2rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    borderBottom: '2px solid rgba(255,255,255,0.3)',
                    paddingBottom: 1
                }}
              >
                {t('home.cta.title')}
              </Typography>
              </Box>
              <Typography variant="h6" paragraph sx={{ 
                color: 'rgba(255,255,255,0.9)', 
                mb: 4,
                lineHeight: 1.6,
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                fontWeight: 300,
                maxWidth: '90%',
                position: 'relative',
                pl: 4,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.8), rgba(255,255,255,0.2))',
                  borderRadius: '3px'
                }
              }}>
                {t('home.cta.subtitle')}
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2.5, 
                  p: 1.5, 
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(5px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.15)',
                    transform: 'translateX(5px)'
                  }
                }}>
                  <Box sx={{ 
                    mr: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    background: 'rgba(255,255,255,0.2)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    <AccessTimeIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  </Box>
                  <Typography variant="body1" sx={{ 
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif', 
                    color: 'rgba(255,255,255,0.9)', 
                    fontWeight: 300, 
                    fontSize: '0.95rem' 
                  }}>
                    {t('home.cta.freeConsultation')}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2.5, 
                  p: 1.5, 
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(5px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.15)',
                    transform: 'translateX(5px)'
                  }
                }}>
                  <Box sx={{ 
                    mr: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    background: 'rgba(255,255,255,0.2)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    <MarkEmailReadIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  </Box>
                  <Typography variant="body1" sx={{ 
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif', 
                    color: 'rgba(255,255,255,0.9)', 
                    fontWeight: 300, 
                    fontSize: '0.95rem' 
                  }}>
                    {t('home.cta.detailedProposal')}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 1.5, 
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(5px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.15)',
                    transform: 'translateX(5px)'
                  }
                }}>
                  <Box sx={{ 
                    mr: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    background: 'rgba(255,255,255,0.2)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}>
                    <AttachMoneyIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  </Box>
                  <Typography variant="body1" sx={{ 
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif', 
                    color: 'rgba(255,255,255,0.9)', 
                    fontWeight: 300, 
                    fontSize: '0.95rem' 
                  }}>
                    {t('home.cta.transparentPricing')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper 
                elevation={0} 
                component="form" 
                onSubmit={handleSubmit}
                sx={{ 
                  p: { xs: 3, md: 5 }, 
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.15)',
                  '& .MuiInputLabel-root': {
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    fontWeight: 300
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    fontWeight: 300
                  },
                  '& .MuiMenuItem-root': {
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    fontWeight: 300
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 50, 
                    height: 50, 
                    borderRadius: '50%', 
                    background: 'rgba(255,255,255,0.2)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    mr: 2
                  }}>
                    <MessageIcon sx={{ fontSize: 28, color: 'white' }} />
                  </Box>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                      mb: 0, 
                    fontWeight: 600,
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      letterSpacing: '0.5px',
                      color: 'white',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -8,
                        left: 0,
                        width: '60px',
                        height: '3px',
                        background: 'rgba(255,255,255,0.5)',
                        borderRadius: '3px'
                      }
                  }}
                >
                  {t('home.contact.title')}
                </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      required
                      fullWidth
                      id="name"
                      label={t('home.contact.fullName')}
                      variant="outlined"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <PersonIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }} />
                        ),
                      }}
                      sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.5)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      required
                      fullWidth
                      id="email"
                      label={t('home.contact.email')}
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <EmailIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }} />
                        ),
                      }}
                      sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.5)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      id="phone"
                      label={t('home.contact.phone')}
                      variant="outlined"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <PhoneIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }} />
                        ),
                      }}
                      sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.5)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      id="company"
                      label={t('home.contact.company')}
                      variant="outlined"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <BusinessIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }} />
                        ),
                      }}
                      sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.5)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControl 
                      fullWidth 
                      sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.5)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'rgba(255,255,255,0.9)',
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255,255,255,0.7)',
                        }
                      }}
                    >
                      <InputLabel id="project-type-label" sx={{ color: 'rgba(255,255,255,0.7)', pl: 4 }}>{t('home.contact.projectType')}</InputLabel>
                      <Select
                        labelId="project-type-label"
                        id="project-type"
                        value={projectType}
                        label={t('home.contact.projectType')}
                        onChange={handleProjectTypeChange}
                        sx={{ color: 'white' }}
                        startAdornment={
                          <CategoryIcon sx={{ ml: 1, mr: 1, color: 'rgba(255,255,255,0.7)' }} />
                        }
                      >
                        <MenuItem value="mobile-app">{t('home.contact.mobileApp')}</MenuItem>
                        <MenuItem value="web-app">{t('home.contact.webApp')}</MenuItem>
                        <MenuItem value="enterprise">{t('home.contact.enterprise')}</MenuItem>
                        <MenuItem value="cloud">{t('home.contact.cloud')}</MenuItem>
                        <MenuItem value="other">{t('home.contact.other')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      required
                      fullWidth
                      id="message"
                      label={t('home.contact.message')}
                      multiline
                      rows={4}
                      variant="outlined"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ pt: 1.5, pr: 1 }}>
                            <MessageIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                          </Box>
                        ),
                      }}
                      sx={{ 
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255,255,255,0.8)',
                          },
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.5)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    />
                  </Grid>
                </Grid>
                
                <Button 
                  type="submit"
                  variant="contained" 
                  size="large"
                  endIcon={
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 30, 
                        height: 30, 
                        borderRadius: '50%', 
                        bgcolor: 'rgba(255,255,255,0.3)',
                        ml: 1
                      }}
                    >
                      <SendIcon sx={{ fontSize: 16, color: 'white' }} />
                    </Box>
                  }
                  sx={{ 
                    py: 1.5,
                    px: 4,
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    textTransform: 'none',
                    letterSpacing: '0.3px',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                    backdropFilter: 'blur(5px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: -100,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transition: 'all 0.5s ease',
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)',
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                      transform: 'translateY(-2px)',
                      '&::before': {
                        left: '100%',
                      }
                    }
                  }}
                >
                  {t('home.contact.send')}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
        
        {/* Success/Error messages */}
        <Snackbar 
          open={formSubmitted} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {t('home.contact.success')}
          </Alert>
        </Snackbar>
        
        <Snackbar 
          open={formError} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
            {t('home.contact.error')}
          </Alert>
        </Snackbar>
      </Box>

      {/* Testimonials Section */}
      <Box 
        id="testimonials"
        sx={{ 
        py: { xs: 8, md: 0 },
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        backgroundImage: 'url(/screen2.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          zIndex: 0
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
              variant="h3" 
              component="h2" 
            gutterBottom 
            sx={{ 
                mb: 2,
              fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: 600,
                fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2.2rem' }
            }}
          >
            {t('home.testimonials.title')}
          </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
            {testimonials.map((testimonial, index) => (
              <Box 
                key={index} 
                sx={{ 
                  width: { xs: '100%', md: '33.33%' }, 
                  px: 2, 
                  mb: { xs: 4, md: 0 }
                }}
              >
                <TestimonialCard
                  quote={testimonial.quote}
                  author={testimonial.author}
                  position={testimonial.position}
                  avatar={testimonial.avatar}
                  index={index}
                />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

// Mini Simulator Component with state management
const MiniSimulator = () => {
  const { t } = useLanguage();
  
  // State for selections
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [userCount, setUserCount] = useState<string>('');
  
  // Platform options
  const platformOptions = [
    { value: 'web', label: 'Web', icon: '/resources/logo/web.png' },
    { value: 'windows', label: 'Windows', icon: '/resources/logo/windows.png' },
    { value: 'android', label: 'Android', icon: '/resources/logo/playstore.png' },
    { value: 'ios', label: 'iOS', icon: '/resources/logo/applestore.png' },
    { value: 'android_tablet', label: 'Android Tablet', icon: '/resources/logo/playstore.png' },
    { value: 'ipados', label: 'iPadOS', icon: '/resources/logo/applestore.png' }
  ];
  
  // User count options
  const userCountOptions = [
    { value: '10', label: '< 10', multiplier: 0.8 },
    { value: '50', label: '10-50', multiplier: 1.0 },
    { value: '100', label: '50-100', multiplier: 1.2 },
    { value: '500', label: '> 100', multiplier: 1.5 }
  ];
  
  // Handle platform selection
  const handlePlatformToggle = (platformValue: string) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platformValue)) {
        return prev.filter(p => p !== platformValue);
      } else {
        return [...prev, platformValue];
      }
    });
  };
  
  return (
    <Box>
      {/* Simulator content */}
      <Box sx={{ p: 3, bgcolor: 'white' }}>
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: '#015480',
              mb: 1
            }}
          >
            {t('home.simulator.title')}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontWeight: 400
            }}
          >
            {t('home.simulator.subtitle')}
          </Typography>
        </Box>
        
        {/* Platforms selection */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center', 
              fontWeight: 600,
              color: '#015480'
            }}
          >
            <CategoryIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('simulator.platforms')}
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
            mb: 2
          }}>
            {platformOptions.map((platform) => (
              <Box
                key={platform.value}
                onClick={() => handlePlatformToggle(platform.value)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: selectedPlatforms.includes(platform.value) 
                    ? '2px solid #015480' 
                    : '1px solid #e0e0e0',
                  bgcolor: selectedPlatforms.includes(platform.value) 
                    ? 'rgba(1, 84, 128, 0.1)' 
                    : 'white',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                    bgcolor: selectedPlatforms.includes(platform.value) 
                      ? 'rgba(1, 84, 128, 0.15)' 
                      : 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                <img
                  src={platform.icon}
                  alt={platform.label}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 4,
                    marginBottom: 8,
                    filter: selectedPlatforms.includes(platform.value) ? 'none' : 'grayscale(50%)'
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: selectedPlatforms.includes(platform.value) ? 600 : 400,
                    color: selectedPlatforms.includes(platform.value) ? '#015480' : 'text.primary'
                  }}
                >
                  {platform.label}
                </Typography>
              </Box>
            ))}
          </Box>
          
          {selectedPlatforms.length > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              {t('simulator.selectedPlatforms')}: {selectedPlatforms.length}
            </Typography>
          )}
        </Box>
        
        {/* User count selection */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 600,
              color: '#015480'
            }}
          >
            <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('common.users')}
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 2,
            mb: 2
          }}>
            {userCountOptions.map((option) => (
              <Box
                key={option.value}
                onClick={() => setUserCount(option.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: userCount === option.value 
                    ? '2px solid #015480' 
                    : '1px solid #e0e0e0',
                  bgcolor: userCount === option.value 
                    ? 'rgba(1, 84, 128, 0.1)' 
                    : 'white',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                    bgcolor: userCount === option.value 
                      ? 'rgba(1, 84, 128, 0.15)' 
                      : 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: userCount === option.value ? 600 : 400,
                    color: userCount === option.value ? '#015480' : 'text.primary'
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        
        {/* Selected platforms and user count summary */}
        <Box sx={{ mb: 4 }}>
          {selectedPlatforms.length > 0 && userCount && (
            <Box sx={{ 
              p: 3, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #f8fafc 0%, #edf7ff 100%)',
              border: '1px solid #e2e8f0',
              display: 'flex', 
              flexDirection: 'column',
              gap: 2
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#015480' }}>
                {t('simulator.selectedPlatforms')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedPlatforms.map(platform => (
                  <Chip
                    key={platform}
                    size="medium"
                    label={platformOptions.find(p => p.value === platform)?.label}
                    sx={{ bgcolor: 'rgba(1, 84, 128, 0.1)', color: '#015480' }}
                    avatar={
                      <Avatar 
                        alt={platformOptions.find(p => p.value === platform)?.label} 
                        src={platformOptions.find(p => p.value === platform)?.icon}
                        sx={{ width: 28, height: 28 }}
                      />
                    }
                  />
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ color: '#015480', mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t('common.users')}: {userCountOptions.find(opt => opt.value === userCount)?.label}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        
        {/* CTA Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Tooltip title={t('home.simulator.description')} arrow placement="top">
            <Button
              component={Link}
              to="/cost-simulator"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<CalculateIcon />}
              endIcon={<ArrowForwardIosIcon />}
              sx={{
                py: 1.8,
                px: 4,
                fontWeight: 700,
                fontSize: '1rem',
                borderRadius: '50px',
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(1, 84, 128, 0.4)',
                background: 'linear-gradient(135deg, #015480 0%, #0288d1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #01436a 0%, #0277bd 100%)',
                  boxShadow: '0 6px 18px rgba(1, 84, 128, 0.5)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('home.simulator.fullVersion')}
            </Button>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Browser footer/status bar */}
      <Box
        sx={{
          bgcolor: '#f0f4f8',
          borderTop: '1px solid #ddd',
          p: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: '#64748b',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ fontSize: 12, mr: 0.5 }} />
          {t('footer.serviceAvailable')}
        </Box>
        <Box>© {new Date().getFullYear()} AresCodeWorks</Box>
      </Box>
    </Box>
  );
};

export default Home; 